import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import { 
  Mail, Phone, Clock, Send, CheckCircle2, Sparkles, MessageSquare, Zap
} from 'lucide-react';

export const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreed) {
      alert('Please agree to the Terms & Conditions, Privacy Policy, and Disclaimer before submitting.');
      return;
    }

    setIsSubmitting(true);

    const form = e.target;
    const formData = new FormData(form);

    try {
      // Check if we're on Netlify (production) or local development
      const isNetlify = window.location.hostname.includes('netlify') || 
                        window.location.hostname.includes('hrnest') ||
                        window.location.hostname !== 'localhost';
      
      if (isNetlify) {
        // Netlify form submission
        const response = await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData).toString(),
        });

        if (response.ok) {
          setSubmitted(true);
        } else {
          throw new Error('Form submission failed');
        }
      } else {
        // Local development - simulate success after brief delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-hr-warm-bg to-white py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        
        <Card className="relative max-w-md w-full mx-4 border-0 shadow-2xl text-center overflow-hidden animate-scale-in">
          <CardContent className="p-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6 animate-bounce-subtle">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mb-4">
              Message Sent!
            </h2>
            <p className="text-hr-text-secondary mb-8">
              Thank you for reaching out. We'll get back to you within 24-48 hours.
            </p>
            <Link to="/">
              <Button className="bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-8 py-5 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-purple-light/20 relative" data-testid="contact-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <MessageSquare className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Let's Connect</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              Get in <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Touch</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Have questions about HR Nest? We'd love to hear from you. Fill out the form and we'll be in touch within 24-48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="contact-form-section">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              {[
                { icon: Mail, title: 'Email Us', value: 'HRnestllc@gmail.com', href: 'mailto:HRnestllc@gmail.com', color: 'purple' },
                { icon: Phone, title: 'Call Us', value: '317-567-3580', href: 'tel:317-567-3580', color: 'teal' },
                { icon: Clock, title: 'Response Time', value: '24-48 hours', href: null, color: 'purple' },
              ].map((item, index) => (
                <Card 
                  key={index}
                  className="group border-0 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 ${
                        item.color === 'purple' ? 'bg-hr-purple-light group-hover:bg-hr-purple' : 'bg-hr-teal-light group-hover:bg-hr-teal'
                      }`}>
                        <item.icon className={`h-6 w-6 transition-colors duration-500 ${
                          item.color === 'purple' ? 'text-hr-purple group-hover:text-white' : 'text-hr-teal group-hover:text-white'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-hr-text-primary mb-1">{item.title}</h3>
                        {item.href ? (
                          <a href={item.href} className="text-hr-teal hover:underline transition-colors">
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-hr-text-secondary">{item.value}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-2xl overflow-hidden animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
                <CardContent className="p-8 lg:p-12 relative">
                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-hr-purple/5 to-transparent rounded-bl-full" />
                  
                  <form 
                    name="contact" 
                    method="POST" 
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    className="relative"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden">
                      <label>
                        Don't fill this out if you're human: <input name="bot-field" />
                      </label>
                    </p>

                    <div className="grid sm:grid-cols-2 gap-6 mb-6">
                      <div className="space-y-2 group">
                        <Label htmlFor="firstName" className="text-hr-text-primary">
                          First Name <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          id="firstName" 
                          name="firstName" 
                          type="text" 
                          required 
                          className="rounded-xl border-stone-200 focus:border-hr-purple focus:ring-hr-purple transition-all duration-300 hover:border-hr-purple/50"
                          data-testid="contact-firstname"
                        />
                      </div>
                      <div className="space-y-2 group">
                        <Label htmlFor="lastName" className="text-hr-text-primary">
                          Last Name <span className="text-red-500">*</span>
                        </Label>
                        <Input 
                          id="lastName" 
                          name="lastName" 
                          type="text" 
                          required 
                          className="rounded-xl border-stone-200 focus:border-hr-purple focus:ring-hr-purple transition-all duration-300 hover:border-hr-purple/50"
                          data-testid="contact-lastname"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 mb-6">
                      <Label htmlFor="email" className="text-hr-text-primary">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        required 
                        className="rounded-xl border-stone-200 focus:border-hr-purple focus:ring-hr-purple transition-all duration-300 hover:border-hr-purple/50"
                        data-testid="contact-email"
                      />
                    </div>

                    <div className="space-y-2 mb-6">
                      <Label htmlFor="subject" className="text-hr-text-primary">
                        Subject <span className="text-red-500">*</span>
                      </Label>
                      <Input 
                        id="subject" 
                        name="subject" 
                        type="text" 
                        required 
                        className="rounded-xl border-stone-200 focus:border-hr-purple focus:ring-hr-purple transition-all duration-300 hover:border-hr-purple/50"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div className="space-y-2 mb-6">
                      <Label htmlFor="message" className="text-hr-text-primary">
                        Your Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea 
                        id="message" 
                        name="message" 
                        rows={6} 
                        required 
                        className="rounded-xl border-stone-200 focus:border-hr-purple focus:ring-hr-purple resize-none transition-all duration-300 hover:border-hr-purple/50"
                        data-testid="contact-message"
                      />
                    </div>

                    {/* Terms Checkbox */}
                    <div className="flex items-start space-x-3 mb-6 p-4 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors duration-300">
                      <Checkbox 
                        id="terms" 
                        name="terms"
                        checked={agreed}
                        onCheckedChange={setAgreed}
                        className="mt-1 data-[state=checked]:bg-hr-purple data-[state=checked]:border-hr-purple"
                        data-testid="contact-terms-checkbox"
                      />
                      <Label htmlFor="terms" className="text-sm text-hr-text-secondary leading-relaxed cursor-pointer">
                        I have read and agree to the{' '}
                        <Link to="/terms" className="text-hr-purple hover:underline">Terms & Conditions</Link>,{' '}
                        <Link to="/privacy" className="text-hr-purple hover:underline">Privacy Policy</Link>, and{' '}
                        <Link to="/disclaimer" className="text-hr-purple hover:underline">Disclaimer</Link>.
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                    </div>

                    {/* Email Opt-in */}
                    <div className="flex items-start space-x-3 mb-8 p-4 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors duration-300">
                      <Checkbox 
                        id="emailOptIn" 
                        name="emailOptIn"
                        className="mt-1 data-[state=checked]:bg-hr-purple data-[state=checked]:border-hr-purple"
                        data-testid="contact-optin-checkbox"
                      />
                      <Label htmlFor="emailOptIn" className="text-sm text-hr-text-secondary leading-relaxed cursor-pointer">
                        I agree to receive emails from HR Nest and understand I can unsubscribe at any time.
                      </Label>
                    </div>

                    <Button 
                      type="submit" 
                      className="group w-full bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!agreed || isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
